import { unwrapResult } from '@reduxjs/toolkit';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { updateCustomerData } from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import {
  APIPaths,
  LeaseApplication,
  PrivateCustomer,
} from '../../../../types/instant-lease-api';
import { getText } from '../../../../utils/getter';
import { errorLogging } from '../../../../utils/logging';
import Navigation from '../../../../utils/navigation';
import IncomeTemplate, {
  IncomeTemplateData,
} from '../../templates/income-template/income-template';

interface IncomeAndExpenseDataProps {
  getPrivateCustomer: (
    leaseApp: LeaseApplication | null,
  ) => PrivateCustomer | undefined;
}

const IncomeAndExpenses: React.FC<IncomeAndExpenseDataProps> = ({
  getPrivateCustomer,
}) => {
  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Init history hook (used in nav)
  const navigate = useNavigate();

  // Navigation class
  const { nextRoute } = new Navigation();

  // Dispatch hook
  const dispatch = useAppDispatch();

  // Get person
  const privateCustomer = getPrivateCustomer(activeApplication);

  const [loading, setLoading] = useState<boolean>(false);

  // OnSubmit
  const submitData = (data: IncomeTemplateData) => {
    setLoading(true);
    dispatch(
      updateCustomerData({
        accessToken,
        leaseApplicationId: activeApplication?.uuid || '',
        inputValue: {
          income: {
            currency_code: 'EUR',
            units: data.income,
            nanos: null,
          },
          additional_income: {
            currency_code: 'EUR',
            units: data.additional_income,
            nanos: null,
          },
          housing_cost: {
            currency_code: 'EUR',
            units: data.housing_cost,
            nanos: null,
          },
          additional_cost: {
            currency_code: 'EUR',
            units: data.additional_cost,
            nanos: null,
          },
        },
        path: APIPaths.INCOME_AND_COST,
      }),
    )
      .then(unwrapResult)
      .then(() => {
        setLoading(false);
        navigate(nextRoute());
      })
      .catch(() => {
        setLoading(false);
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: true,
            notificationTitle: 'Das tut uns leid',
            notificationBody: getText('error_unable_to_process_data'),
          }),
        );
        errorLogging(
          new Error(
            `unable to submit income and expenses to user with application id ${activeApplication?.uuid}`,
          ),
        );
      });
  };

  return (
    <Layout stepStatus heading='Einnahmen & Ausgaben'>
      <IncomeTemplate
        tabButton={false}
        onSubmit={submitData}
        privateCustomer={privateCustomer}
        loading={loading}
      />
    </Layout>
  );
};

export default IncomeAndExpenses;
